import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Page,
  Navbar,
  NavbarBackLink,
  Block,
  BlockTitle,
  List,
  ListItem,
  ListInput,
  Segmented,
  SegmentedButton,
  Toggle,
  Range,
  Button,
} from 'konsta/react'
import {
  Spade,
  Users,
  Lock,
  Percent,
  DollarSign,
  Layers,
  KeyRound,
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'

// Blind level definitions
const BLIND_LEVELS = [
  { small: 100, big: 200, label: '$1/$2' },
  { small: 200, big: 500, label: '$2/$5' },
  { small: 500, big: 1000, label: '$5/$10' },
  { small: 1000, big: 2000, label: '$10/$20' },
  { small: 2500, big: 5000, label: '$25/$50' },
]

type GameType = 'NLH' | 'PLO'
type SeatCount = 6 | 9

export function CreateRoomScreen() {
  const navigate = useNavigate()

  // Form state
  const [gameType, setGameType] = useState<GameType>('NLH')
  const [roomName, setRoomName] = useState('')
  const [blindIndex, setBlindIndex] = useState(1) // Default to $2/$5
  const [seatCount, setSeatCount] = useState<SeatCount>(9)
  const [isPrivate, setIsPrivate] = useState(false)
  const [roomPassword, setRoomPassword] = useState('')
  const [agentCommission, setAgentCommission] = useState(0) // 0-10 (represents 0%-5%, step 0.5)
  const [isCreating, setIsCreating] = useState(false)

  // Derived values
  const currentBlind = BLIND_LEVELS[blindIndex]

  const buyInRange = useMemo(() => {
    const bb = currentBlind.big
    return {
      min: bb * 40, // 40 BB min
      max: bb * 200, // 200 BB max
    }
  }, [currentBlind])

  const commissionPercent = agentCommission * 0.5 // Convert slider value to percent

  const rakePercent = 5 // Fixed 5% rake

  const isFormValid = roomName.trim().length >= 2 && (!isPrivate || roomPassword.length >= 4)

  const handleCreate = () => {
    if (!isFormValid) return

    setIsCreating(true)

    // Simulate creation delay
    setTimeout(() => {
      setIsCreating(false)
      navigate('/rooms', {
        state: {
          toast: {
            message: `房間「${roomName}」已創建成功！`,
            type: 'success',
          },
        },
      })
    }, 1000)
  }

  return (
    <Page className="bg-[#F2F2F7]">
      <Navbar
        title="創建房間"
        left={<NavbarBackLink onClick={() => navigate(-1)} />}
      />

      {/* Game Type */}
      <BlockTitle className="!mt-2">遊戲類型</BlockTitle>
      <Block strong inset className="!py-3">
        <Segmented strong rounded>
          <SegmentedButton
            active={gameType === 'NLH'}
            onClick={() => setGameType('NLH')}
          >
            <span className="flex items-center gap-1.5">
              <Spade className="w-3.5 h-3.5" />
              NLH
            </span>
          </SegmentedButton>
          <SegmentedButton
            active={gameType === 'PLO'}
            onClick={() => setGameType('PLO')}
          >
            <span className="flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" />
              PLO
            </span>
          </SegmentedButton>
        </Segmented>
        <p className="text-xs text-[#8E8E93] text-center mt-2">
          {gameType === 'NLH' ? '無限注德州撲克' : '底池限注奧馬哈'}
        </p>
      </Block>

      {/* Room Name */}
      <BlockTitle>房間資訊</BlockTitle>
      <List strongIos insetIos>
        <ListInput
          label="房間名稱"
          type="text"
          placeholder="輸入房間名稱"
          value={roomName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRoomName(e.target.value)}
          info={roomName.trim().length < 2 ? '至少 2 個字元' : ''}
          clearButton={roomName.length > 0}
          onClear={() => setRoomName('')}
          media={
            <div className="w-7 h-7 rounded-md bg-[#007AFF] flex items-center justify-center">
              <Spade className="w-4 h-4 text-white" />
            </div>
          }
        />
      </List>

      {/* Blind Level */}
      <BlockTitle>盲注級別: {currentBlind.label}</BlockTitle>
      <List strongIos insetIos>
        <ListItem
          innerClassName="flex flex-col gap-3 w-full"
          innerChildren={
            <div className="w-full">
              {/* Blind labels row */}
              <div className="flex justify-between items-center mb-3">
                <span className="text-xs text-[#8E8E93]">$1/$2</span>
                <span className="text-sm font-semibold text-[#007AFF]">
                  {currentBlind.label}
                </span>
                <span className="text-xs text-[#8E8E93]">$25/$50</span>
              </div>
              <Range
                value={blindIndex}
                min={0}
                max={BLIND_LEVELS.length - 1}
                step={1}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setBlindIndex(Number(e.target.value))
                }
              />
              {/* Tick marks */}
              <div className="flex justify-between mt-1 px-0.5">
                {BLIND_LEVELS.map((_, i) => (
                  <div
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${
                      i === blindIndex ? 'bg-[#007AFF]' : 'bg-[#C7C7CC]'
                    }`}
                  />
                ))}
              </div>
            </div>
          }
        />
      </List>

      {/* Buy-in Range Display */}
      <BlockTitle>買入範圍</BlockTitle>
      <List strongIos insetIos>
        <ListItem
          title="最少買入"
          media={
            <div className="w-7 h-7 rounded-md bg-[#34C759] flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-white" />
            </div>
          }
          after={
            <span className="text-sm font-medium text-black">
              {formatCurrency(buyInRange.min)}
            </span>
          }
        />
        <ListItem
          title="最多買入"
          media={
            <div className="w-7 h-7 rounded-md bg-[#FF9500] flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-white" />
            </div>
          }
          after={
            <span className="text-sm font-medium text-black">
              {formatCurrency(buyInRange.max)}
            </span>
          }
        />
        <ListItem
          title="大盲"
          after={
            <span className="text-sm text-[#8E8E93]">
              {formatCurrency(currentBlind.big)}
            </span>
          }
        />
      </List>

      {/* Seat Count */}
      <BlockTitle>座位數</BlockTitle>
      <Block strong inset className="!py-3">
        <Segmented strong rounded>
          <SegmentedButton
            active={seatCount === 6}
            onClick={() => setSeatCount(6)}
          >
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              6 人桌
            </span>
          </SegmentedButton>
          <SegmentedButton
            active={seatCount === 9}
            onClick={() => setSeatCount(9)}
          >
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              9 人桌
            </span>
          </SegmentedButton>
        </Segmented>
      </Block>

      {/* Privacy Settings */}
      <BlockTitle>隱私設定</BlockTitle>
      <List strongIos insetIos>
        <ListItem
          label
          title="私人房間"
          subtitle="僅受邀玩家可加入"
          media={
            <div className="w-7 h-7 rounded-md bg-[#FF3B30] flex items-center justify-center">
              <Lock className="w-4 h-4 text-white" />
            </div>
          }
          after={
            <Toggle
              component="div"
              className="-my-1"
              checked={isPrivate}
              onChange={() => setIsPrivate(!isPrivate)}
            />
          }
        />
      </List>

      {/* Private Room Options */}
      {isPrivate && (
        <>
          <List strongIos insetIos>
            <ListInput
              label="房間密碼"
              type="password"
              placeholder="設定 4-8 位密碼"
              value={roomPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setRoomPassword(e.target.value)
              }
              info={
                roomPassword.length > 0 && roomPassword.length < 4
                  ? '密碼至少 4 位'
                  : ''
              }
              media={
                <div className="w-7 h-7 rounded-md bg-[#FF9500] flex items-center justify-center">
                  <KeyRound className="w-4 h-4 text-white" />
                </div>
              }
            />
          </List>

          {/* Agent Commission */}
          <BlockTitle>代理佣金: {commissionPercent.toFixed(1)}%</BlockTitle>
          <List strongIos insetIos>
            <ListItem
              innerClassName="flex flex-col gap-3 w-full"
              innerChildren={
                <div className="w-full">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs text-[#8E8E93]">0%</span>
                    <span className="text-sm font-semibold text-[#AF52DE]">
                      {commissionPercent.toFixed(1)}%
                    </span>
                    <span className="text-xs text-[#8E8E93]">5%</span>
                  </div>
                  <Range
                    value={agentCommission}
                    min={0}
                    max={10}
                    step={1}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setAgentCommission(Number(e.target.value))
                    }
                  />
                </div>
              }
            />
          </List>
        </>
      )}

      {/* Rake Display */}
      <BlockTitle>費用摘要</BlockTitle>
      <List strongIos insetIos>
        <ListItem
          title="抽水比例（平台）"
          media={
            <div className="w-7 h-7 rounded-md bg-[#8E8E93] flex items-center justify-center">
              <Percent className="w-4 h-4 text-white" />
            </div>
          }
          after={
            <span className="text-sm font-medium text-black">
              {rakePercent}%（固定）
            </span>
          }
        />
        {isPrivate && commissionPercent > 0 && (
          <ListItem
            title="代理佣金"
            media={
              <div className="w-7 h-7 rounded-md bg-[#AF52DE] flex items-center justify-center">
                <Percent className="w-4 h-4 text-white" />
              </div>
            }
            after={
              <span className="text-sm font-medium text-[#AF52DE]">
                +{commissionPercent.toFixed(1)}%
              </span>
            }
          />
        )}
        <ListItem
          title="總費率"
          after={
            <span className="text-sm font-semibold text-black">
              {isPrivate
                ? `${(rakePercent + commissionPercent).toFixed(1)}%`
                : `${rakePercent}%`}
            </span>
          }
        />
      </List>

      {/* Summary Card */}
      <div className="mx-4 mt-2 bg-white rounded-2xl p-4">
        <h3 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wide mb-3">
          房間摘要
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-[#8E8E93]">遊戲類型</span>
            <span className="text-sm font-medium text-black">
              {gameType === 'NLH' ? '無限注德州撲克' : '底池限注奧馬哈'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#8E8E93]">盲注</span>
            <span className="text-sm font-medium text-black">{currentBlind.label}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#8E8E93]">買入</span>
            <span className="text-sm font-medium text-black">
              {formatCurrency(buyInRange.min)} - {formatCurrency(buyInRange.max)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#8E8E93]">座位</span>
            <span className="text-sm font-medium text-black">{seatCount} 人</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#8E8E93]">類型</span>
            <span className="text-sm font-medium text-black">
              {isPrivate ? '私人' : '公開'}
            </span>
          </div>
          {isPrivate && commissionPercent > 0 && (
            <div className="flex justify-between">
              <span className="text-sm text-[#8E8E93]">總費率</span>
              <span className="text-sm font-medium text-black">
                {(rakePercent + commissionPercent).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Create Button */}
      <div className="mx-4 mt-6 mb-8">
        <Button
          large
          rounded
          onClick={handleCreate}
          disabled={!isFormValid || isCreating}
        >
          {isCreating ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              創建中...
            </span>
          ) : (
            '創建房間'
          )}
        </Button>
      </div>
    </Page>
  )
}
